<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="assistant-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('assistant.header.badge') }}</div>
                    <h2>{{ $t('assistant.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('assistant.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="assistant-container">
                <!-- Hero Section -->
                <div class="hero-section">
                    <div class="hero-content">
                        <div class="ai-badge">
                            <v-icon size="24">mdi-brain</v-icon>
                            <span>{{ $t('assistant.hero.poweredBy') }}</span>
                        </div>
                        <h3>{{ $t('assistant.hero.title') }}</h3>
                        <p>{{ $t('assistant.hero.description') }}</p>
                    </div>
                </div>

                <!-- Key Differentiators -->
                <div class="differentiators-section">
                    <div class="section-header">
                        <h3>{{ $t('assistant.differentiators.title') }}</h3>
                        <p>{{ $t('assistant.differentiators.subtitle') }}</p>
                    </div>

                    <div class="differentiators-grid">
                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-database-cog</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.realData.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.realData.description') }}</p>
                        </div>

                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-lightning-bolt</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.instantActions.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.instantActions.description') }}</p>
                        </div>

                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-account-cog</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.contextAware.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.contextAware.description') }}</p>
                        </div>

                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-chart-timeline-variant</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.visualIntegration.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.visualIntegration.description') }}</p>
                        </div>

                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-devices</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.universalAccess.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.universalAccess.description') }}</p>
                        </div>

                        <div class="diff-card">
                            <div class="diff-icon">
                                <v-icon size="32" color="white">mdi-brain</v-icon>
                            </div>
                            <h4>{{ $t('assistant.differentiators.alwaysLearning.title') }}</h4>
                            <p>{{ $t('assistant.differentiators.alwaysLearning.description') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Core Capabilities -->
                <div class="capabilities-section">
                    <div class="section-header">
                        <h3>{{ $t('assistant.capabilities.title') }}</h3>
                        <p>{{ $t('assistant.capabilities.subtitle') }}</p>
                    </div>

                    <v-row class="capabilities-grid">
                        <!-- Speech & Language -->
                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="capability-card speech">
                                <div class="capability-icon">
                                    <v-icon size="40" color="white">mdi-microphone-message</v-icon>
                                </div>
                                <h4>{{ $t('assistant.capabilities.naturalLanguage.title') }}</h4>
                                <p>{{ $t('assistant.capabilities.naturalLanguage.description') }}</p>
                                <div class="capability-list">
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.naturalLanguage.feature1') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.naturalLanguage.feature2') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.naturalLanguage.feature3') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.naturalLanguage.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Smart Booking -->
                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="capability-card booking">
                                <div class="capability-icon">
                                    <v-icon size="40" color="white">mdi-calendar-check</v-icon>
                                </div>
                                <h4>{{ $t('assistant.capabilities.smartBooking.title') }}</h4>
                                <p>{{ $t('assistant.capabilities.smartBooking.description') }}</p>
                                <div class="capability-list">
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.smartBooking.feature1') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.smartBooking.feature2') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.smartBooking.feature3') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.smartBooking.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Issue Reporting -->
                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="capability-card reporting">
                                <div class="capability-icon">
                                    <v-icon size="40" color="white">mdi-alert-circle-check-outline</v-icon>
                                </div>
                                <h4>{{ $t('assistant.capabilities.issueReporting.title') }}</h4>
                                <p>{{ $t('assistant.capabilities.issueReporting.description') }}</p>
                                <div class="capability-list">
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.issueReporting.feature1') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.issueReporting.feature2') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.issueReporting.feature3') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.issueReporting.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Integrated Commands -->
                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="capability-card commands">
                                <div class="capability-icon">
                                    <v-icon size="40" color="white">mdi-console-line</v-icon>
                                </div>
                                <h4>{{ $t('assistant.capabilities.integratedCommands.title') }}</h4>
                                <p>{{ $t('assistant.capabilities.integratedCommands.description') }}</p>
                                <div class="capability-list">
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.integratedCommands.feature1') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.integratedCommands.feature2') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.integratedCommands.feature3') }}</span>
                                    </div>
                                    <div class="capability-item">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('assistant.capabilities.integratedCommands.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Conversation Examples -->
                <div class="examples-section">
                    <div class="section-header">
                        <h3>{{ $t('assistant.examples.title') }}</h3>
                        <p>{{ $t('assistant.examples.subtitle') }}</p>
                    </div>

                    <div class="examples-grid">
                        <div class="example-card">
                            <div class="user-message">
                                <v-icon size="20">mdi-account</v-icon>
                                <span>{{ $t('assistant.examples.example1.user') }}</span>
                            </div>
                            <div class="assistant-message">
                                <v-icon size="20">mdi-robot</v-icon>
                                <div class="message-content">
                                    <span>{{ $t('assistant.examples.example1.assistant') }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="example-card">
                            <div class="user-message">
                                <v-icon size="20">mdi-account</v-icon>
                                <span>{{ $t('assistant.examples.example2.user') }}</span>
                            </div>
                            <div class="assistant-message">
                                <v-icon size="20">mdi-robot</v-icon>
                                <div class="message-content">
                                    <span>{{ $t('assistant.examples.example2.assistant') }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="example-card">
                            <div class="user-message">
                                <v-icon size="20">mdi-account</v-icon>
                                <span>{{ $t('assistant.examples.example3.user') }}</span>
                            </div>
                            <div class="assistant-message">
                                <v-icon size="20">mdi-robot</v-icon>
                                <div class="message-content">
                                    <span>{{ $t('assistant.examples.example3.assistant') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Command Examples -->
                <div class="commands-section">
                    <div class="section-header">
                        <h3>{{ $t('assistant.commands.title') }}</h3>
                        <p>{{ $t('assistant.commands.subtitle') }}</p>
                    </div>

                    <div class="commands-chips">
                        <div class="command-chip booking-chip">
                            <v-icon size="18">mdi-calendar</v-icon>
                            <span>{{ $t('assistant.commands.command1') }}</span>
                        </div>
                        <div class="command-chip navigation-chip">
                            <v-icon size="18">mdi-map-marker</v-icon>
                            <span>{{ $t('assistant.commands.command2') }}</span>
                        </div>
                        <div class="command-chip analytics-chip">
                            <v-icon size="18">mdi-chart-line</v-icon>
                            <span>{{ $t('assistant.commands.command3') }}</span>
                        </div>
                        <div class="command-chip support-chip">
                            <v-icon size="18">mdi-help-circle</v-icon>
                            <span>{{ $t('assistant.commands.command4') }}</span>
                        </div>
                        <div class="command-chip status-chip">
                            <v-icon size="18">mdi-information</v-icon>
                            <span>{{ $t('assistant.commands.command5') }}</span>
                        </div>
                        <div class="command-chip control-chip">
                            <v-icon size="18">mdi-lightbulb</v-icon>
                            <span>{{ $t('assistant.commands.command6') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Visual & Interactive -->
                <div class="visual-section">
                    <div class="section-header">
                        <h3>{{ $t('assistant.visual.title') }}</h3>
                        <p>{{ $t('assistant.visual.subtitle') }}</p>
                    </div>

                    <v-row class="visual-grid">
                        <v-col cols="12" md="4" class="pa-3">
                            <div class="visual-card">
                                <v-icon size="48" color="#1E5A8E">mdi-chart-box</v-icon>
                                <h4>{{ $t('assistant.visual.dataVisualization.title') }}</h4>
                                <p>{{ $t('assistant.visual.dataVisualization.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="4" class="pa-3">
                            <div class="visual-card">
                                <v-icon size="48" color="#1E5A8E">mdi-calendar-text</v-icon>
                                <h4>{{ $t('assistant.visual.interactiveDialogs.title') }}</h4>
                                <p>{{ $t('assistant.visual.interactiveDialogs.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="4" class="pa-3">
                            <div class="visual-card">
                                <v-icon size="48" color="#1E5A8E">mdi-map-marker-path</v-icon>
                                <h4>{{ $t('assistant.visual.spatialNavigation.title') }}</h4>
                                <p>{{ $t('assistant.visual.spatialNavigation.description') }}</p>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Learning & Improvement -->
                <div class="learning-section">
                    <div class="learning-content">
                        <div class="learning-icon">
                            <v-icon size="64" color="white">mdi-brain</v-icon>
                        </div>
                        <div class="learning-text">
                            <h3>{{ $t('assistant.learning.title') }}</h3>
                            <p>{{ $t('assistant.learning.description') }}</p>
                            <div class="learning-benefits">
                                <div class="benefit">
                                    <v-icon>mdi-chart-timeline-variant</v-icon>
                                    <span>{{ $t('assistant.learning.benefit1') }}</span>
                                </div>
                                <div class="benefit">
                                    <v-icon>mdi-account-group</v-icon>
                                    <span>{{ $t('assistant.learning.benefit2') }}</span>
                                </div>
                                <div class="benefit">
                                    <v-icon>mdi-lightbulb-on</v-icon>
                                    <span>{{ $t('assistant.learning.benefit3') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() { 
    visible.value = true; 
}

function closeDialog() { 
    visible.value = false; 
}

defineExpose({ open, close: closeDialog });
</script>
<style scoped>
/* Card Base */
.assistant-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.assistant-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Hero Section */
.hero-section {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    border-radius: 24px;
    padding: 48px;
    text-align: center;
    margin-bottom: 56px;
    box-shadow: 0 8px 32px rgba(30, 90, 142, 0.2);
}

.ai-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 24px;
    color: white;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 24px;
    backdrop-filter: blur(10px);
}

.hero-content h3 {
    font-size: 36px;
    font-weight: 800;
    color: white;
    margin: 0 0 20px 0;
    letter-spacing: -0.5px;
}

.hero-content p {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 900px;
    margin: 0 auto;
}

/* Section Styling */
.differentiators-section,
.capabilities-section,
.intelligence-section,
.examples-section,
.commands-section,
.visual-section,
.learning-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Differentiators */
.differentiators-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
}

.diff-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    text-align: center;
    transition: all 0.3s ease;
    border: 2px solid #f3f4f6;
}

.diff-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #1E5A8E;
}

.diff-icon {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}

.diff-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.diff-card p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    line-height: 1.6;
}

/* Capability Cards */
.capability-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.capability-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.capability-card.speech:hover { border-color: #06b6d4; }
.capability-card.booking:hover { border-color: #10b981; }
.capability-card.reporting:hover { border-color: #ef4444; }
.capability-card.commands:hover { border-color: #8b5cf6; }

.capability-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.capability-card:hover .capability-icon {
    transform: scale(1.1) rotate(-5deg);
}

.capability-card.speech .capability-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.capability-card.booking .capability-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.capability-card.reporting .capability-icon {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.capability-card.commands .capability-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.capability-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
    text-align: center;
}

.capability-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
    text-align: center;
}

.capability-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.capability-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.capability-item .v-icon {
    color: #10b981;
}

/* Intelligence Cards */
.intelligence-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    height: 100%;
    border: 2px solid #f3f4f6;
    transition: all 0.3s ease;
}

.intelligence-card:hover {
    border-color: #1E5A8E;
    box-shadow: 0 8px 24px rgba(30, 90, 142, 0.12);
}

.intelligence-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.intelligence-header h4 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.intelligence-card > p {
    font-size: 15px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.intelligence-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.intelligence-features .feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #4b5563;
    font-weight: 500;
}

.intelligence-features .feature .v-icon {
    color: #1E5A8E;
}

/* Example Cards */
.examples-grid {
    display: grid;
    gap: 24px;
}

.example-card {
    background: white;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.user-message {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #f3f4f6;
    border-radius: 12px;
    margin-bottom: 16px;
}

.user-message .v-icon {
    color: #1E5A8E;
}

.user-message span {
    font-size: 15px;
    color: #1f2937;
    font-weight: 500;
}

.assistant-message {
    display: flex;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    border-radius: 12px;
}

.assistant-message .v-icon {
    color: white;
    margin-top: 2px;
}

.message-content span {
    font-size: 15px;
    color: white;
    line-height: 1.6;
}

/* Command Chips */
.commands-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
}

.command-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-radius: 24px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    cursor: pointer;
}

.command-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.booking-chip { background: #dbeafe; color: #1e40af; }
.booking-chip:hover { background: #bfdbfe; }

.navigation-chip { background: #fef3c7; color: #92400e; }
.navigation-chip:hover { background: #fde68a; }

.analytics-chip { background: #d1fae5; color: #065f46; }
.analytics-chip:hover { background: #a7f3d0; }

.support-chip { background: #fee2e2; color: #991b1b; }
.support-chip:hover { background: #fecaca; }

.status-chip { background: #e0e7ff; color: #3730a3; }
.status-chip:hover { background: #c7d2fe; }

.control-chip { background: #fae8ff; color: #6b21a8; }
.control-chip:hover { background: #f3e8ff; }

/* Visual Cards */
.visual-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    text-align: center;
    border: 2px solid #f3f4f6;
    transition: all 0.3s ease;
    height: 100%;
}

.visual-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #1E5A8E;
}

.visual-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 16px 0 12px 0;
}

.visual-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
}

/* Learning Section */
.learning-section {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
    border-radius: 24px;
    padding: 48px;
    margin-bottom: 0;
}

.learning-content {
    display: flex;
    align-items: center;
    gap: 40px;
}

.learning-icon {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    backdrop-filter: blur(10px);
}

.learning-text h3 {
    font-size: 28px;
    font-weight: 800;
    color: white;
    margin: 0 0 16px 0;
}

.learning-text > p {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    margin: 0 0 24px 0;
}

.learning-benefits {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.benefit {
    display: flex;
    align-items: center;
    gap: 12px;
    color: white;
    font-size: 15px;
    font-weight: 500;
}

.benefit .v-icon {
    color: white;
}

/* Responsive */
@media (max-width: 1024px) {
    .assistant-container {
        padding: 32px 24px;
    }

    .hero-section {
        padding: 32px 24px;
    }

    .learning-content {
        flex-direction: column;
        text-align: center;
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .assistant-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .hero-content h3 {
        font-size: 28px;
    }

    .hero-content p {
        font-size: 16px;
    }

    .differentiators-grid {
        grid-template-columns: 1fr;
    }

    .intelligence-header {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .assistant-container {
        padding: 20px 12px;
    }

    .hero-section {
        padding: 24px 16px;
    }

    .learning-section {
        padding: 32px 20px;
    }
}

/* Scrollbar */
.assistant-container::-webkit-scrollbar {
    width: 8px;
}

.assistant-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.assistant-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.assistant-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>